import { Modal } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearButtonProcessing,
  resetAlert,
  setAlertSuccess,
  setButtonProcessing,
} from "../../store/actions";
import { createTemplate } from "../../services/TemplateServices";
import {
  TextInput,
  Label,
  FormGroup,
  FormGroupVerticalInput,
  SelectInput,
  ErrorMessageAlert,
  Button,
} from "../../components";
import {
  errorStatusHandle,
  getAlertErrorMessage,
  getFormErrorResponseData,
  getInputErrorMessage,
  hasErrorInput,
} from "../../utils";
import { getSelectLabel } from "../../utils/select-utils";
import Select2 from "../Select2";
import {
  getChildGroupOptionConfig,
  hasErrors,
} from "../../utils/group-option-utils";
import * as ArrayUtils from "../../utils/array-utils";

function ModalAddNewTemplate({
  openModal,
  setOpenModal,
  settingData,
  setNewTemplate,
}) {
  // Initialize state for input values
  const dispatch = useDispatch();
  const groupSettings = useSelector(
    (state) => state.app_settings.machine_group
  );
  const [modalInputs, setModalInputs] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const OPTION_NO_SPECIFY = { value: "", label: "[ALL]" };
  const [groupsOptions, setGroupOptions] = useState({});

  useEffect(() => {
    let groupCombine = {
      1: modalInputs.group_1,
      2: modalInputs.group_2,
      3: modalInputs.group_3,
      4: modalInputs.group_4,
      5: modalInputs.group_5,
    };
    const options = getChildGroupOptionConfig(
      settingData.groupsChildren,
      groupCombine,
      settingData.groupsList,
      settingData.machines
    );
    setGroupOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalInputs, settingData]);

  // Function to handle changes in input
  const onChange = (e, fieldName) => {
    dispatch(resetAlert());
    let value;
    if (fieldName === "machine_id") {
      value = e ? e.value : "";
    } else {
      value = e.target.value;
    }
    setModalInputs((prevValues) => {
      const updatedData = { ...prevValues };
      if (fieldName.startsWith("group")) {
        let x = fieldName.split("_").pop();
        updatedData[fieldName] = value.toString();
        x = parseInt(x);
        for (let i = 1; i <= 5; i++) {
          if (groupSettings["group_" + i].active) {
            if (i < x) {
              if (prevValues["group_" + i]) {
                updatedData["group_" + i] = prevValues["group_" + i];
              } else {
                updatedData["group_" + i] = "";
              }
            } else if (i === x) {
              updatedData["group_" + i] = value.toString();
            } else {
              updatedData["group_" + i] = "";
            }
          }
        }
        updatedData["machine_id"] = "";
      } else {
        updatedData[fieldName] = value.toString();
        if (fieldName === "machine_id") {
          updatedData[fieldName] = value.toString();
          for (let i = 1; i <= 5; i++) {
            if (groupSettings["group_" + i].active) {
              updatedData["group_" + i] = prevValues["group_" + i];
            }
          }
        }
      }
      return updatedData;
    });
  };

  // Function to handle closing modal
  const handleClose = () => {
    setErrorMessages({});
    setOpenModal(false);
  };

  // Function to handle add new template
  const handleAddTemplate = async (e, buttonId) => {
    e.preventDefault();
    dispatch(setButtonProcessing(buttonId));
    dispatch(resetAlert());
    await createTemplate(modalInputs)
      .then((response) => {
        setErrorMessages({});
        setModalInputs({});
        dispatch(setAlertSuccess(response.message));
        setNewTemplate(response.data.template);
        setOpenModal(false);
      })
      .catch((e) => {
        if (e.response.status === 400) {
          let errorResponse = getFormErrorResponseData(e);
          if (errorResponse) {
            setErrorMessages(errorResponse);
          }
        }
        errorStatusHandle(e.response);
      })
      .finally(() => {
        dispatch(clearButtonProcessing(buttonId));
      });
  };

  return (
    <Modal dismissible show={openModal} onClose={handleClose}>
      <Modal.Header className="items-center h-[50px] bg-white border-tenken-gray-200">
        <p className="text-[18px] lg:text-xl">新しい点検テンプレートを追加する</p>
      </Modal.Header>
      <Modal.Body className="overflow-x-hidden">
        <ErrorMessageAlert
          className="mb-[16px]"
          error={getAlertErrorMessage(errorMessages)}
        />
        <form>
          <FormGroup>
            <Label
              text="点検テンプレート名"
              className="modal-add__label w-[200px]"
              htmlFor="modal-name"
              required={true}
            />
            <FormGroupVerticalInput
              error={getInputErrorMessage(errorMessages.errors, "name")}
            >
              <TextInput
                name="name"
                id="modal-name"
                placeholder="点検テンプレートの名前..."
                className="lg:w-[380px] md:w-[340px]"
                maxLength={50}
                value={modalInputs["name"] || ""}
                hasError={hasErrorInput(errorMessages.errors, "name")}
                onChange={(e) => onChange(e, "name")}
              />
            </FormGroupVerticalInput>
          </FormGroup>
          {ArrayUtils.range(1, 5).map(
            (i, idx) =>
              groupSettings[`group_${i}`]?.active && (
                <FormGroup key={idx}>
                  <Label
                    text={groupSettings[`group_${i}`]?.name}
                    required={i === 1}
                    htmlFor="modal-group_2"
                    className="modal-add__label w-[200px]"
                  />
                  <FormGroupVerticalInput
                    error={getInputErrorMessage(
                      errorMessages.errors,
                      `group_${i}`
                    )}
                    className="flex-col xl:w-[380px] md:w-[340px]"
                  >
                    <SelectInput
                      options={groupsOptions[i]}
                      name={`group_${i}`}
                      className="template-management__add--select"
                      hasError={hasErrors(
                        [`group_${i}`, "department_group", "group_error"],
                        errorMessages.errors
                      )}
                      value={modalInputs[`group_${i}`] || ""}
                      onChange={(e) => onChange(e, `group_${i}`)}
                    />
                  </FormGroupVerticalInput>
                </FormGroup>
              )
          )}
          <FormGroup>
            <Label
              text={groupSettings.machine?.name}
              htmlFor="modal-machine_id"
              className="modal-add__label w-[200px]"
            />
            <FormGroupVerticalInput
              error={getInputErrorMessage(errorMessages.errors, "machine_id")}
              className="flex-col xl:w-[380px] md:w-[340px]"
            >
              <Select2
                className="template-management__add--select"
                classNamePrefix="react-select"
                options={groupsOptions?.machines}
                value={
                  modalInputs["machine_id"]
                    ? {
                        value: parseInt(modalInputs["machine_id"]),
                        label: getSelectLabel(
                          groupsOptions?.machines,
                          parseInt(modalInputs["machine_id"])
                        ),
                      }
                    : OPTION_NO_SPECIFY
                }
                hasError={hasErrors(
                  ["machine_id", "department_group"],
                  errorMessages.errors
                )}
                name="machine_id"
                onChange={(e) => onChange(e, "machine_id")}
              />
            </FormGroupVerticalInput>
          </FormGroup>
        </form>
      </Modal.Body>
      <Modal.Footer className="space-x-[32px] h-[80px]">
        <div className="w-1/2">
          <Button color="light" className="w-full" onClick={handleClose}>
            閉じる
          </Button>
        </div>
        <div className="w-1/2">
          <Button
            color="primary"
            className="w-full"
            onClick={(e) => handleAddTemplate(e, "create-template")}
            id="create-template"
          >
            新規追加
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalAddNewTemplate;
